/**
 * A rehype plugin that turns `[[mention:...]]` markers inside hast text nodes
 * into the same chip compound the editor renders — the old web's
 * `rehypeMentions.ts`. Used for the assistant's messages, which are markdown
 * *and* may quote a mention; walking text nodes means no element type needs
 * enumerating.
 */
import { visit } from "unist-util-visit";
import type { Element, Properties, Root, Text } from "hast";
import {
  MENTION_WRAPPER_CLASS,
  MINOR_CHIP_CLASS,
  MINOR_CHIP_OVERFLOW_CLASS,
  getMajorChipClass,
  getMajorChipText,
  getMajorChipTooltip,
  getMinorChipTooltip,
  getOverflowTooltip,
  splitParamsForDisplay,
} from "./mentionChipPresentation";
import { decodeLabel, isSkillMentionType, parseFamilyType, parseParams, type MentionParameter } from "./mentionUtils";

/**
 * The React side's answer to "does this item have parameters?" — the transform
 * has no React context of its own, so the caller closes over its cache and
 * hands a plain function down. `null` means unknown → the optimistic blue.
 */
export type ExtractabilityLookup = (type: string, id: string) => boolean | null | undefined;

function textSpan(value: string): Element {
  return { type: "element", tagName: "span", properties: { className: ["revit-mention-text"] }, children: [{ type: "text", value }] };
}

function buildMajorChip(type: string, id: string, label: string, enrich: ExtractabilityLookup | undefined): Element {
  const probe = isSkillMentionType(type) ? false : enrich ? enrich(type, id) : undefined;
  return {
    type: "element",
    tagName: "span",
    properties: { className: getMajorChipClass(probe ?? undefined).split(" "), title: getMajorChipTooltip(type, id) },
    children: [textSpan(getMajorChipText(label))],
  };
}

function buildMinorChip(param: MentionParameter): Element {
  const properties: Properties = { className: [MINOR_CHIP_CLASS], title: getMinorChipTooltip(param), "data-param-id": param.id };
  if (param.scope) properties["data-param-scope"] = param.scope;
  return { type: "element", tagName: "span", properties, children: [textSpan(param.name)] };
}

function buildOverflowChip(overflow: MentionParameter[]): Element {
  const tooltip = getOverflowTooltip(overflow);
  return {
    type: "element",
    tagName: "span",
    properties: { className: [MINOR_CHIP_OVERFLOW_CLASS], title: tooltip, "aria-label": `${overflow.length} more parameters: ${tooltip}` },
    children: [{ type: "text", value: `+${overflow.length}` }],
  };
}

export function buildMentionChipElement(
  type: string,
  id: string,
  label: string,
  params: MentionParameter[],
  familyName: string | undefined,
  typeName: string | undefined,
  enrich: ExtractabilityLookup | undefined,
): Element {
  const properties: Properties = {
    className: [MENTION_WRAPPER_CLASS],
    "data-type": type,
    "data-id": id,
    "data-label": label,
    "aria-label": `Mention: ${label}, Type: ${type}, ID: ${id}`,
  };
  if (familyName) properties["data-family-name"] = familyName;
  if (typeName) properties["data-type-name"] = typeName;
  // The full list, always — overflow is a display concern; a paste back into
  // the editor rebuilds `selectedParams` from this attribute.
  if (params.length > 0) properties["data-params"] = JSON.stringify(params);

  const { visible, overflow } = splitParamsForDisplay(params);
  const children: Element[] = [buildMajorChip(type, id, label, enrich), ...visible.map(buildMinorChip)];
  if (overflow.length > 0) children.push(buildOverflowChip(overflow));
  return { type: "element", tagName: "span", properties, children };
}

/** A text value split at its markers: text nodes and chip elements. */
export function textToHastNodes(value: string, patternSource: string, enrich: ExtractabilityLookup | undefined): Array<Text | Element> {
  const pattern = new RegExp(patternSource, "g");
  const nodes: Array<Text | Element> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = null;
  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) nodes.push({ type: "text", value: value.slice(lastIndex, match.index) });
    const type = match[1] ?? "";
    const rawId = match[2] ?? "";
    const { familyName, typeName } = parseFamilyType(match[5]);
    nodes.push(
      buildMentionChipElement(
        type,
        isSkillMentionType(type) ? decodeLabel(rawId) : rawId,
        decodeLabel(match[3] ?? ""),
        parseParams(match[4]),
        familyName,
        typeName,
        enrich,
      ),
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < value.length) nodes.push({ type: "text", value: value.slice(lastIndex) });
  return nodes;
}

/**
 * The plugin factory: `[rehypeMentions, MENTION_PATTERN_SOURCE, enrich]` in
 * react-markdown's `rehypePlugins`.
 */
export function rehypeMentions(patternSource: string, enrich?: ExtractabilityLookup) {
  return (tree: Root | undefined) => {
    if (!tree || typeof tree !== "object" || !("children" in tree)) return;
    const test = new RegExp(patternSource);
    const replacements: Array<{ parent: { children: unknown[] }; index: number; nodes: Array<Text | Element> }> = [];
    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || typeof index !== "number") return;
      if (!test.test(node.value ?? "")) return;
      replacements.push({ parent: parent as unknown as { children: unknown[] }, index, nodes: textToHastNodes(node.value, patternSource, enrich) });
    });
    // Back to front, so earlier indices stay valid while later ones are spliced.
    for (let i = replacements.length - 1; i >= 0; i--) {
      const { parent, index, nodes } = replacements[i]!;
      parent.children.splice(index, 1, ...nodes);
    }
  };
}
